import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import {
  Camera, FileSpreadsheet, FileText, Plus, Trash2, Edit3,
  Image as ImageIcon, ChevronDown, Eye, X, Folder, Share2, Archive, List, LayoutList,
  Send, CheckCircle2, Clock, ExternalLink, Zap,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ShareModal from "@/components/app/ShareModal";
import InlineScanDrop from "@/components/app/InlineScanDrop";

const CATEGORY_LABELS = {
  golf: "Golf",
  promotion: "Promotion",
  representation_compagnie: "Représentation - Compagnie",
  representation_repas: "Représentation - Repas",
  association: "Association",
  hotel_avion: "Hôtel / Avion",
  auto_essence: "Auto / Essence",
  stationnement: "Stationnement / Taxi",
  salon_congres: "Salon / Congrès",
  bureau: "Bureau",
  informatique: "Informatique",
  communication: "Communication",
  formation: "Formation",
};

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMonth, setFilterMonth] = useState("");
  const [reportId, setReportId] = useState(searchParams.get("report") || "");
  const [exportLoading, setExportLoading] = useState(null);
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: null });
  const [shareOpen, setShareOpen] = useState(false);
  const [compact, setCompact] = useState(() => localStorage.getItem("dashboard_compact") === "1");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTargetReport, setBulkTargetReport] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const { user } = useAuth();

  useEffect(() => { localStorage.setItem("dashboard_compact", compact ? "1" : "0"); }, [compact]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const bulkAssign = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      await api.post("/invoices/bulk/assign-report", {
        invoice_ids: [...selectedIds],
        report_id: bulkTargetReport || null,
      });
      toast.success(`${selectedIds.size} facture(s) ${bulkTargetReport ? "assignée(s)" : "retirée(s) du rapport"}`);
      clearSelection();
      setBulkTargetReport("");
      load();
      loadReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'assignation");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedIds.size} facture(s) ? Cette action est irréversible.`)) return;
    setBulkBusy(true);
    try {
      await api.post("/invoices/bulk/delete", { invoice_ids: [...selectedIds] });
      toast.success(`${selectedIds.size} facture(s) supprimée(s)`);
      clearSelection();
      load();
      loadReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setBulkBusy(false);
    }
  };

  const activeReport = useMemo(
    () => reports.find((r) => r.report_id === reportId) || null,
    [reports, reportId]
  );

  const loadReports = useCallback(async () => {
    try {
      const { data } = await api.get("/reports");
      setReports(data);
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterMonth) params.month = filterMonth;
      if (reportId) params.report_id = reportId;
      const { data } = await api.get("/invoices", { params });
      setInvoices(data);
    } catch (_e) {
      toast.error("Impossible de charger les factures");
    } finally {
      setLoading(false);
    }
  }, [filterMonth, reportId]);

  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (reportId) setSearchParams({ report: reportId });
    else setSearchParams({});
  }, [reportId, setSearchParams]);

  const months = useMemo(() => {
    const set = new Set();
    invoices.forEach((i) => { if (i.date) set.add(i.date.slice(0, 7)); });
    return Array.from(set).sort().reverse();
  }, [invoices]);

  const sorted = useMemo(
    () => [...invoices].sort((a, b) => (a.date || "").localeCompare(b.date || "")),
    [invoices]
  );

  const totals = useMemo(() => {
    const sum = sorted.reduce((s, x) => s + (Number(x.total) || 0), 0);
    const count = sorted.length;
    const withImage = sorted.filter((x) => x.image_filename).length;
    return { sum, count, withImage };
  }, [sorted]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      setInvoices((prev) => prev.filter((i) => i.invoice_id !== id));
      toast.success("Facture supprimée");
    } catch { toast.error("Suppression impossible"); }
  };

  const submitReport = async () => {
    if (!activeReport) return;
    if (!window.confirm(`Soumettre « ${activeReport.name} » pour validation par l'administrateur ?`)) return;
    try {
      await api.post(`/reports/${activeReport.report_id}/submit`);
      toast.success("Rapport envoyé pour validation");
      loadReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la soumission");
    }
  };

  const unsubmitReport = async () => {
    if (!activeReport) return;
    try {
      await api.post(`/reports/${activeReport.report_id}/unsubmit`);
      toast.success("Rapport remis en mode brouillon");
      loadReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const assignToReport = async (invoiceId, newReportId) => {
    try {
      await api.put(`/invoices/${invoiceId}`, { report_id: newReportId || null });
      toast.success("Rapport mis à jour");
      load();
      loadReports();
    } catch { toast.error("Échec"); }
  };

  const buildExportPayload = () => ({
    month: filterMonth || undefined,
    report_id: reportId || undefined,
    employee_name: user?.name,
    company: "LABORATOIRE RIVA",
  });

  const [optimizing, setOptimizing] = useState(false);
  const optimizeImages = async () => {
    if (!window.confirm("Compresser les anciennes photos volumineuses de ce compte ? Sans perte visible — ça accélère grandement la génération de PDF.")) return;
    setOptimizing(true);
    try {
      const { data } = await api.post("/users/me/compress-images", null, { timeout: 600000 });
      const candidates = data.candidates ?? data.processed ?? 0;
      if (data.processed > 0) {
        toast.success(`✨ ${data.processed} photo(s) compressée(s) — ${data.saved_mb || 0} Mo économisés. Réessayez le PDF !`);
      } else if (candidates === 0) {
        toast.success("Toutes les photos sont déjà optimisées ✓");
      } else if (data.failures > 0) {
        toast.warning(`${data.failures} photo(s) n'ont pas pu être compressées. ${data.processed} traitées.`);
      } else {
        toast.info(`${candidates} photo(s) candidate(s) — déjà au minimum.`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = !e?.response
        ? "La compression prend du temps — réessayez dans 1 minute, elle continue en arrière-plan."
        : (detail || `Erreur ${e?.response?.status || "?"}`);
      toast.error(msg);
    } finally {
      setOptimizing(false);
    }
  };

  const doExport = async (kind, { preview = false } = {}) => {
    if (!invoices.length) { toast.error("Aucune facture dans la liste"); return; }
    setExportLoading(kind + (preview ? "_preview" : ""));
    const url = kind === "pdf" ? "/export/pdf" : kind === "zip" ? "/export/zip" : "/export/excel";
    const mime = kind === "pdf" ? "application/pdf" : kind === "zip" ? "application/zip" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const ext = kind === "pdf" ? "pdf" : kind === "zip" ? "zip" : "xlsx";
    const scope = reportId ? activeReport?.name || "rapport" : filterMonth || "export";
    const filenameBase = kind === "zip" ? "compte_depenses" : kind === "pdf" ? "factures" : "compte_depenses";
    const filename = `${filenameBase}_${scope}.${ext}`;

    // Retry once on network error (iOS Safari + cellular occasionally drops the first request)
    const requestExport = async () =>
      api.post(url, buildExportPayload(), { responseType: "blob", timeout: 180000 });

    try {
      let res;
      try {
        res = await requestExport();
      } catch (e1) {
        if (!e1?.response) {
          toast.info("Lien instable — nouvelle tentative...");
          await new Promise((r) => setTimeout(r, 1200));
          res = await requestExport();
        } else {
          throw e1;
        }
      }
      const blob = new Blob([res.data], { type: mime });

      if (kind === "pdf" && preview) {
        const objUrl = URL.createObjectURL(blob);
        setPdfPreview({ open: true, url: objUrl, blob, filename });
      } else {
        // Universal download (desktop + iOS Safari + Android Chrome)
        const objUrl = URL.createObjectURL(blob);
        const dl = document.createElement("a");
        dl.href = objUrl;
        dl.download = filename;
        dl.rel = "noopener";
        dl.target = "_blank";  // iOS Safari fallback: opens if download attr isn't honored
        document.body.appendChild(dl);
        dl.click();
        dl.remove();
        // Keep objUrl alive long enough for iOS to actually save
        setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
        toast.success(`Export ${kind.toUpperCase()} prêt`);
      }
    } catch (e) {
      // Error body comes as a Blob when responseType is blob — parse it
      let detail = e?.response?.data?.detail;
      if (!detail && e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          detail = parsed?.detail || text;
        } catch { /* ignore */ }
      }
      if (!detail && !e?.response) {
        detail = "Génération trop longue ou connexion perdue. Essayez avec un filtre par mois pour réduire le volume.";
      }
      toast.error(typeof detail === "string" ? detail : (e?.message || "Export impossible"));
    } finally {
      setExportLoading(null);
    }
  };

  const closePreview = () => {
    if (pdfPreview.url) URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview({ open: false, url: null });
  };

  const downloadFromPreview = () => {
    if (!pdfPreview.blob) return;
    const objUrl = URL.createObjectURL(pdfPreview.blob);
    const dl = document.createElement("a");
    dl.href = objUrl;
    dl.download = pdfPreview.filename || `factures_${reportId ? activeReport?.name || "rapport" : filterMonth || "export"}.pdf`;
    dl.rel = "noopener";
    dl.target = "_blank";
    document.body.appendChild(dl);
    dl.click();
    dl.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
  };

  const openPreviewInNewTab = () => {
    if (!pdfPreview.url) return;
    window.open(pdfPreview.url, "_blank", "noopener");
  };

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="overline mb-2">{activeReport ? `Rapport · ${activeReport.name}` : "Tableau de bord"}</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none">
            {activeReport ? activeReport.name : "Vos factures."}
          </h1>
          <p className="text-gray-500 mt-3 max-w-lg">
            Triées par date, automatiquement. Prévisualisez en PDF ou exportez en Excel format Riva-Avir.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/scan" className="btn-primary" data-testid="cta-scan">
            <Camera size={16} /> Scanner une facture
          </Link>
        </div>
      </div>

      {/* Report status banner */}
      {activeReport && (
        <div
          className={
            "ra-card p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 " +
            (activeReport.status === "approved" ? "border-l-4 !border-l-green-500" :
             activeReport.status === "rejected" ? "border-l-4 !border-l-amber-500 bg-amber-50/60 dark:bg-amber-950/20" :
             activeReport.status === "ready_for_review" ? "border-l-4 !border-l-amber-500" : "")
          }
          data-testid="report-status-banner"
        >
          <div className="flex items-start gap-3">
            {activeReport.status === "approved" && <CheckCircle2 size={22} className="text-green-600 shrink-0 mt-0.5" />}
            {activeReport.status === "rejected" && <Send size={22} className="text-amber-600 shrink-0 mt-0.5" />}
            {activeReport.status === "ready_for_review" && <Clock size={22} className="text-amber-600 shrink-0 mt-0.5" />}
            {!["approved", "rejected", "ready_for_review"].includes(activeReport.status) && <Edit3 size={20} className="text-gray-500 shrink-0 mt-0.5" />}
            <div>
              <div className="font-semibold">
                {activeReport.status === "approved" && "Rapport validé par l'administrateur"}
                {activeReport.status === "rejected" && "Rapport à corriger — notes de l'administrateur"}
                {activeReport.status === "ready_for_review" && "En attente de validation"}
                {!["approved", "rejected", "ready_for_review"].includes(activeReport.status) && "Brouillon — modifiable"}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {activeReport.status === "ready_for_review" && activeReport.submitted_at && (
                  <>Soumis le {String(activeReport.submitted_at).slice(0,10)}</>
                )}
                {activeReport.status === "approved" && activeReport.reviewed_at && (
                  <>Validé le {String(activeReport.reviewed_at).slice(0,10)}{activeReport.reviewed_by_name ? ` par ${activeReport.reviewed_by_name}` : ""}</>
                )}
                {activeReport.status === "rejected" && activeReport.review_comment && (
                  <><strong>Notes :</strong> {activeReport.review_comment}</>
                )}
                {!["approved", "rejected", "ready_for_review"].includes(activeReport.status) && (
                  <>Quand tout est complet, envoyez-le à l'administrateur pour validation.</>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {(!activeReport.status || activeReport.status === "active" || activeReport.status === "rejected") && (
              <button
                className="btn-primary"
                onClick={submitReport}
                disabled={totals.count === 0}
                data-testid="submit-report-button"
              >
                <Send size={14} /> Prêt à valider
              </button>
            )}
            {activeReport.status === "ready_for_review" && (
              <button
                className="btn-secondary"
                onClick={unsubmitReport}
                data-testid="unsubmit-report-button"
              >
                Annuler l'envoi
              </button>
            )}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Factures" value={totals.count} testId="stat-count" />
        <StatCard label="Total ($)" value={totals.sum.toFixed(2)} mono testId="stat-total" />
        <StatCard label="Avec photo" value={`${totals.withImage} / ${totals.count}`} testId="stat-images" />
        <StatCard label={activeReport ? "Rapport" : "Mois filtré"} value={activeReport ? activeReport.name : (filterMonth || "Tous")} testId="stat-scope" />
      </div>

      {/* Drag-and-drop multi-scan */}
      <InlineScanDrop reportId={reportId || null} onComplete={() => { load(); loadReports(); }} />

      {/* Filters + Actions */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="overline" htmlFor="filter-report">Rapport</label>
            <div className="relative">
              <select
                id="filter-report"
                data-testid="filter-report"
                value={reportId}
                onChange={(e) => setReportId(e.target.value)}
                className="ra-input pr-8 appearance-none text-sm"
              >
                <option value="">Tous les rapports</option>
                <option value="none">Sans rapport</option>
                {reports.map((r) => (
                  <option key={r.report_id} value={r.report_id}>
                    {r.status === "archived" ? "📦 " : ""}{r.name}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="overline" htmlFor="filter-month">Mois</label>
            <div className="relative">
              <select
                id="filter-month"
                data-testid="filter-month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="ra-input pr-8 appearance-none text-sm"
              >
                <option value="">Tous</option>
                {months.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          <button
            className="btn-secondary text-sm justify-center"
            onClick={() => doExport("pdf", { preview: true })}
            disabled={exportLoading?.startsWith("pdf")}
            data-testid="preview-pdf-button"
          >
            <Eye size={14} /> {exportLoading === "pdf_preview" ? "..." : "Aperçu"}
          </button>
          <button
            className="btn-secondary text-sm justify-center"
            onClick={() => doExport("pdf")}
            disabled={exportLoading === "pdf"}
            data-testid="export-pdf-button"
          >
            <FileText size={14} /> {exportLoading === "pdf" ? "..." : "PDF"}
          </button>
          <button
            className="btn-secondary text-sm justify-center"
            onClick={() => doExport("excel")}
            disabled={exportLoading === "excel"}
            data-testid="export-excel-button"
          >
            <FileSpreadsheet size={14} /> {exportLoading === "excel" ? "..." : "Excel"}
          </button>
          <button
            className="btn-secondary text-sm justify-center"
            onClick={() => doExport("zip")}
            disabled={exportLoading === "zip"}
            data-testid="export-zip-button"
          >
            <Archive size={14} /> {exportLoading === "zip" ? "..." : "ZIP"}
          </button>
          <button
            className="btn-primary text-sm justify-center col-span-2 sm:col-span-1"
            onClick={() => setShareOpen(true)}
            data-testid="share-button"
          >
            <Share2 size={14} /> Partager
          </button>
          <button
            className="btn-ghost text-sm justify-center"
            onClick={() => setCompact((c) => !c)}
            title={compact ? "Vue détaillée" : "Vue compacte"}
            data-testid="toggle-compact"
          >
            {compact ? <LayoutList size={14} /> : <List size={14} />}
            <span className="hidden sm:inline">{compact ? "Détaillée" : "Compacte"}</span>
          </button>
        </div>

        {/* Image optimization helper — visible if PDF generation is failing */}
        <div className="mt-3 p-3 ra-card border-l-4 !border-l-[#002FA7] flex items-start sm:items-center gap-3 flex-col sm:flex-row">
          <div className="flex-1 min-w-0">
            <div className="font-heading font-black text-sm tracking-tight">PDF lent ou erreur réseau ?</div>
            <p className="text-xs text-gray-500 mt-0.5">Compresser les anciennes photos volumineuses de ce compte (sans perte visible). À faire 1 seule fois.</p>
          </div>
          <button
            className="btn-secondary text-sm whitespace-nowrap"
            onClick={optimizeImages}
            disabled={optimizing}
            data-testid="optimize-images-button"
          >
            <Zap size={14} /> {optimizing ? "Compression..." : "Optimiser les photos"}
          </button>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div
          className="ra-card p-3 mb-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 border-l-4 !border-l-[#002FA7]"
          data-testid="bulk-actions-bar"
        >
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-heading font-black text-lg num">{selectedIds.size}</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">facture(s) sélectionnée(s)</span>
          </div>
          <div className="grow" />
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="ra-input !py-2 !min-w-[180px]"
              value={bulkTargetReport}
              onChange={(e) => setBulkTargetReport(e.target.value)}
              disabled={bulkBusy}
              data-testid="bulk-target-report"
            >
              <option value="">— Retirer du rapport —</option>
              {reports.filter((r) => r.status !== "archived").map((r) => (
                <option key={r.report_id} value={r.report_id}>{r.name}</option>
              ))}
            </select>
            <button
              type="button"
              className="btn-primary"
              onClick={bulkAssign}
              disabled={bulkBusy}
              data-testid="bulk-assign-button"
            >
              {bulkBusy ? "..." : bulkTargetReport ? "Assigner" : "Retirer"}
            </button>
            <button
              type="button"
              className="btn-secondary text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="bulk-delete-button"
            >
              <Trash2 size={14} /> Supprimer
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={clearSelection}
              disabled={bulkBusy}
              data-testid="bulk-clear-button"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Invoices */}
      <div className="ra-card" data-testid="invoices-table-card">
        {loading && <div className="progress-line" />}
        {(!loading && sorted.length === 0) ? (
          <EmptyState />
        ) : compact ? (
          <div className="divide-y divide-[var(--ra-border)]" data-testid="invoices-compact-list">
            {sorted.map((inv) => (
              <Link
                key={inv.invoice_id}
                to={`/invoice/${inv.invoice_id}`}
                className="flex items-center gap-3 px-3 sm:px-4 py-2 hover:bg-[var(--ra-bg-subtle)] transition-colors text-sm"
                data-testid={`invoice-compact-${inv.invoice_id}`}
              >
                <span className="num text-xs text-[var(--ra-text-secondary)] shrink-0" style={{ width: 84 }}>
                  {inv.date || "—"}
                </span>
                <span className="flex-1 min-w-0 truncate font-medium">{inv.merchant || inv.description || "Sans nom"}</span>
                <span className="hidden sm:inline text-xs text-[var(--ra-text-secondary)] truncate max-w-[180px]">
                  {CATEGORY_LABELS[inv.category] || "—"}
                </span>
                <span className="hidden md:inline text-xs text-[var(--ra-text-secondary)] w-10 text-right">{inv.province}</span>
                {inv.is_restaurant && <span className="hidden sm:inline w-2 h-2 rounded-full bg-amber-400" title="Restaurant" />}
                {inv.image_filename && <ImageIcon size={12} className="text-[var(--ra-text-muted)] shrink-0" />}
                <span className="num font-semibold shrink-0" style={{ width: 84, textAlign: "right" }}>
                  {Number(inv.total || 0).toFixed(2)} $
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <>
            {/* Mobile card view */}
            <div className="md:hidden divide-y divide-[var(--ra-border)]" data-testid="invoices-mobile-list">
              {sorted.length > 0 && (
                <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-neutral-900/50 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#002FA7]"
                    checked={sorted.length > 0 && sorted.every((i) => selectedIds.has(i.invoice_id))}
                    ref={(el) => {
                      if (el) el.indeterminate = selectedIds.size > 0 && !sorted.every((i) => selectedIds.has(i.invoice_id));
                    }}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedIds(new Set(sorted.map((i) => i.invoice_id)));
                      else clearSelection();
                    }}
                    data-testid="select-all-invoices-mobile"
                  />
                  <span className="text-sm font-medium">
                    {selectedIds.size > 0
                      ? `${selectedIds.size} sélectionnée(s)`
                      : "Tout sélectionner"}
                  </span>
                </label>
              )}
              {sorted.map((inv) => {
                const checked = selectedIds.has(inv.invoice_id);
                return (
                <div
                  key={inv.invoice_id}
                  className={`p-4 ${checked ? "bg-blue-50/50 dark:bg-blue-950/30" : ""}`}
                  data-testid={`invoice-row-${inv.invoice_id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4 shrink-0 accent-[#002FA7]"
                      checked={checked}
                      onChange={() => toggleSelect(inv.invoice_id)}
                      data-testid={`select-${inv.invoice_id}`}
                      aria-label="Sélectionner"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="num text-xs text-gray-500">{inv.date || "—"}</span>
                        {inv.is_restaurant && <span className="badge restaurant">Restaurant</span>}
                      </div>
                      <div className="font-semibold truncate">{inv.merchant || inv.description || "Sans nom"}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{CATEGORY_LABELS[inv.category] || "—"} · {inv.province}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="num font-heading font-black text-xl tracking-tight">{Number(inv.total || 0).toFixed(2)} $</div>
                      {(Number(inv.tps) > 0 || Number(inv.tvq) > 0) && (
                        <div className="num text-[10px] text-gray-400">TPS {Number(inv.tps || 0).toFixed(2)} · TVQ {Number(inv.tvq || 0).toFixed(2)}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Link to={`/invoice/${inv.invoice_id}`} className="btn-secondary text-xs flex-1 justify-center" data-testid={`edit-${inv.invoice_id}`}>
                      <Edit3 size={12} /> Modifier
                    </Link>
                    <button onClick={() => remove(inv.invoice_id)} className="btn-danger text-xs" data-testid={`delete-${inv.invoice_id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              );
              })}
            </div>

            {/* Desktop table view */}
            <div className="hidden md:block overflow-x-auto">
            <table className="ra-table">
              <thead>
                <tr>
                  <th style={{width: 36}}>
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-[#002FA7]"
                      checked={sorted.length > 0 && sorted.every((i) => selectedIds.has(i.invoice_id))}
                      ref={(el) => {
                        if (el) el.indeterminate = selectedIds.size > 0 && !sorted.every((i) => selectedIds.has(i.invoice_id));
                      }}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedIds(new Set(sorted.map((i) => i.invoice_id)));
                        else clearSelection();
                      }}
                      data-testid="select-all-invoices"
                      aria-label="Tout sélectionner"
                    />
                  </th>
                  <th style={{width: 90}}>Date</th>
                  <th>Description</th>
                  <th>Catégorie</th>
                  <th>Rapport</th>
                  <th>Participants</th>
                  <th style={{width: 70}}>Prov.</th>
                  <th className="num" style={{width: 110}}>Total</th>
                  <th className="num" style={{width: 90}}>TPS</th>
                  <th className="num" style={{width: 90}}>TVQ</th>
                  <th style={{width: 120}}></th>
                </tr>
              </thead>
              <tbody data-testid="invoices-tbody">
                {sorted.map((inv) => {
                  const checked = selectedIds.has(inv.invoice_id);
                  return (
                  <tr
                    key={inv.invoice_id}
                    className={checked ? "!bg-blue-50/60 dark:!bg-blue-950/20" : ""}
                    data-testid={`invoice-row-desktop-${inv.invoice_id}`}
                  >
                    <td>
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-[#002FA7]"
                        checked={checked}
                        onChange={() => toggleSelect(inv.invoice_id)}
                        data-testid={`select-desktop-${inv.invoice_id}`}
                        aria-label="Sélectionner"
                      />
                    </td>
                    <td className="num">{inv.date || "—"}</td>
                    <td>
                      <div className="font-medium">{inv.merchant || inv.description || "Sans nom"}</div>
                      {inv.is_restaurant && <span className="badge restaurant mt-1">Restaurant</span>}
                    </td>
                    <td>
                      <span className="text-xs text-gray-700">{CATEGORY_LABELS[inv.category] || inv.category || "—"}</span>
                    </td>
                    <td>
                      <select
                        className="text-xs px-2 py-1 border border-gray-200 rounded-sm bg-white max-w-[150px]"
                        value={inv.report_id || ""}
                        onChange={(e) => assignToReport(inv.invoice_id, e.target.value)}
                        data-testid={`assign-report-${inv.invoice_id}`}
                      >
                        <option value="">—</option>
                        {reports.filter((r) => r.status === "active").map((r) => (
                          <option key={r.report_id} value={r.report_id}>{r.name}</option>
                        ))}
                      </select>
                    </td>
                    <td>{inv.participants || <span className="text-gray-400">—</span>}</td>
                    <td>{inv.province || "—"}</td>
                    <td className="num font-semibold">{Number(inv.total || 0).toFixed(2)}</td>
                    <td className="num text-gray-600">{Number(inv.tps || 0).toFixed(2)}</td>
                    <td className="num text-gray-600">{Number(inv.tvq || 0).toFixed(2)}</td>
                    <td>
                      <div className="flex items-center gap-1 justify-end">
                        {inv.image_filename && <ImageIcon size={14} className="text-gray-400" aria-label="Photo attachée" />}
                        <Link to={`/invoice/${inv.invoice_id}`} className="btn-ghost" data-testid={`edit-desktop-${inv.invoice_id}`}>
                          <Edit3 size={14} />
                        </Link>
                        <button onClick={() => remove(inv.invoice_id)} className="btn-ghost text-red-600 hover:bg-red-50" data-testid={`delete-desktop-${inv.invoice_id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>

      <Link to="/scan" className="fab md:hidden" data-testid="fab-scan">
        <Plus size={18} /> Scanner
      </Link>

      {/* PDF Preview Modal */}
      {pdfPreview.open && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-stretch p-2 sm:p-4 md:p-8" data-testid="pdf-preview-modal">
          <div className="bg-white dark:bg-neutral-900 border-2 border-[#0B0F19] dark:border-white w-full flex flex-col">
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-neutral-700 gap-2">
              <div className="min-w-0 flex-1">
                <p className="overline">Aperçu PDF</p>
                <div className="font-heading font-black text-base sm:text-xl tracking-tight truncate">
                  {activeReport?.name || filterMonth || "Export"}
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                <button className="btn-secondary" onClick={openPreviewInNewTab} data-testid="open-preview-new-tab">
                  <ExternalLink size={14} /> <span className="hidden sm:inline">Ouvrir</span>
                </button>
                <button className="btn-primary" onClick={downloadFromPreview} data-testid="download-preview-button">
                  <FileText size={14} /> <span className="hidden sm:inline">Télécharger</span>
                </button>
                <button className="btn-ghost" onClick={closePreview} data-testid="close-preview-button">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-gray-50 dark:bg-neutral-950 relative">
              <iframe
                title="pdf-preview"
                src={pdfPreview.url}
                className="absolute inset-0 w-full h-full"
                data-testid="pdf-preview-iframe"
              />
              {/* Mobile fallback: iframe doesn't work on iOS Safari — big button */}
              <div className="sm:hidden absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-white via-white/90 to-transparent dark:from-neutral-900 dark:via-neutral-900/90 text-center">
                <button className="btn-primary w-full justify-center py-3" onClick={openPreviewInNewTab}>
                  <ExternalLink size={14} /> Ouvrir le PDF en plein écran
                </button>
                <p className="text-xs text-gray-500 mt-2">Sur iPhone/iPad, l'aperçu intégré peut rester blanc — ouvrez en plein écran.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        scope={{ report_id: reportId, month: filterMonth, report_name: activeReport?.name }}
        defaultTitle={activeReport?.name ? `Compte de dépenses — ${activeReport.name}` : filterMonth ? `Compte de dépenses — ${filterMonth}` : "Compte de dépenses"}
      />
    </Layout>
  );
}

function StatCard({ label, value, mono, testId }) {
  return (
    <div className="ra-card p-3 sm:p-4" data-testid={testId}>
      <div className="overline mb-1 sm:mb-2 truncate">{label}</div>
      <div className={`text-xl sm:text-3xl font-heading font-black tracking-tighter truncate ${mono ? "num" : ""}`} title={String(value)}>
        {value}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="p-12 text-center">
      <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 flex items-center justify-center">
        <Camera className="text-gray-400" size={20} />
      </div>
      <p className="font-heading text-xl font-bold mb-1">Aucune facture</p>
      <p className="text-sm text-gray-500 mb-4">Commencez en scannant ou ajoutant manuellement une facture.</p>
      <Link to="/scan" className="btn-primary"><Camera size={14} /> Scanner maintenant</Link>
    </div>
  );
}
